import { Grid } from '@mui/material';
import { DataGrid, GridColDef, gridClasses, GridToolbar } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getExpiredWorkspaceMetrics } from 'wisestep-api';
import { formattedDate, WsColors, WsTypography } from 'wisestep-base';

interface ExpiredPlanMetrics {
  workspaceName: string;
  ownerId: string;
  activeUsersCount: string;
  planCreatedOn: string;
  createdOn: string;
  expiryDate: string;
  usersCount: string;
  totalAcceptedUsers: string;
  planCode: string;
}

const ExpiredWorkspaceInfo = () => {
  const [workspaceData, setWorkspaceData] = useState<ExpiredPlanMetrics[]>([]);

  const tableData = useMemo(() => {
    const data = workspaceData.map((workspaceObj) => ({
      id: uuidv4(),
      name: workspaceObj?.workspaceName,
      ownerId: workspaceObj.ownerId,
      createdOn: workspaceObj?.createdOn ?? 'Not Available',
      planCreatedOn: workspaceObj?.planCreatedOn ?? 'Not Available',
      expiry: workspaceObj?.expiryDate,
      usersCount: Number(workspaceObj?.usersCount == '0' ? '1' : workspaceObj?.usersCount),
      activeUsers: Number(
        dayjs(new Date()).diff(workspaceObj.expiryDate, 'd') <= 30
          ? workspaceObj?.activeUsersCount == '0'
            ? '1'
            : workspaceObj?.activeUsersCount
          : workspaceObj?.activeUsersCount
      ),
      totalAcceptedUsers: Number(
        workspaceObj?.totalAcceptedUsers == '0' ? '1' : workspaceObj?.totalAcceptedUsers
      ),
      planCode: workspaceObj?.planCode,
    }));

    const totalCountOfUsers = data.reduce(
      (prevSum, currentObj) => ({
        usersCount: prevSum.usersCount + currentObj.usersCount,
        activeUsersCount: prevSum.activeUsersCount + currentObj.activeUsers,
        totalAcceptedUsers: prevSum.totalAcceptedUsers + currentObj.totalAcceptedUsers,
      }),
      {
        usersCount: 0,
        activeUsersCount: 0,
        totalAcceptedUsers: 0,
      }
    );
    return { data, totalCountOfUsers };
  }, [workspaceData]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    { field: 'name', headerName: 'Workspace Name', flex: 1 },
    { field: 'ownerId', headerName: 'Owner Email', flex: 1.7 },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
      type: 'date',
      renderCell: (params) => formattedDate(params.row.createdOn),
    },
    {
      field: 'planCreatedOn',
      headerName: 'Plan Created On',
      flex: 1,
      type: 'date',
      renderCell: (params) => formattedDate(params.row.planCreatedOn),
    },
    {
      field: 'expiry',
      headerName: 'Plan Expiry Date',
      flex: 1,
      type: 'date',
      renderCell: (params) => formattedDate(params.row.expiry),
    },
    {
      field: 'usersCount',
      headerName: `Total Invited Users (${tableData.totalCountOfUsers.usersCount})`,
      flex: 1,
    },
    {
      field: 'activeUsers',
      headerName: `Active User in 30 days (${tableData.totalCountOfUsers.activeUsersCount})`,
      flex: 1,
    },
    {
      field: 'totalAcceptedUsers',
      headerName: `Total Activated Users (${tableData.totalCountOfUsers.totalAcceptedUsers})`,
      flex: 1,
    },
    { field: 'planCode', headerName: 'Plan', flex: 1 },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const req = await getExpiredWorkspaceMetrics();
      setWorkspaceData(req.data);
    };
    fetchData();
  }, []);

  return (
    <Grid item xs={12} bgcolor={WsColors.White} height={690}>
      <WsTypography bold>Expired Workspaces with Plan Info</WsTypography>
      <DataGrid
        columns={columns}
        rows={tableData.data}
        components={{ Toolbar: GridToolbar }}
        getRowHeight={() => 'auto'}
        sx={{
          [`& .${gridClasses.cell}`]: {
            py: 2,
          },
        }}
        pageSize={10}
      />
    </Grid>
  );
};

export default ExpiredWorkspaceInfo;
